import { createQueryKeys } from '@lukemorales/query-key-factory';
import Moment from 'moment';
import { getAllMessages } from '../api/claims/claims-bullpen.api';
import {
  getClaims,
  getClaimReserveTotal,
  getClaimInvoiceTotalPaid,
} from '../api/claims/claim.api';
import { getData } from '../utils/functional/fp';

// This intentionally is hardcoded to display specific data for bullpens badge
const notificationFilter = {
  before: Moment().endOf('day').unix(),
  after: Moment().subtract(5, 'year').startOf('day').unix(),
  f: { claimNumber: { not_exists: 'true' } },
  size: '1',
};

/**
 * The lower bound of the claim-number lookup's date window.
 *
 * <p>The claims listing endpoint filters on `modified` and, given no `after`, defaults it to one
 * year ago. A claim nobody has touched in a year would then never resolve, so the lookup asks from
 * a date that predates every claim instead.
 */
const CLAIM_NUMBER_LOOKUP_AFTER = Moment('2018-01-01').unix();

export const claims = createQueryKeys('claims', {
  list: (filters: Record<string, unknown>) => ({
    queryKey: [{ filters }],
    queryFn: () => getClaims({ params: filters }).then(getData),
  }),
  /**
   * The claim with exactly this number, or null when none is visible to the user.
   *
   * <p>`claimNumber` is mapped as text and tokenised on its hyphens, so the filter goes at the
   * keyword subfield; the backend runs a term query on any filter field ending in `.keyword`.
   */
  byNumber: (claimNumber: string) => ({
    queryKey: [claimNumber],
    queryFn: () =>
      getClaims({
        params: {
          f: { 'claimNumber.keyword': { eq: claimNumber } },
          after: CLAIM_NUMBER_LOOKUP_AFTER,
          size: 1,
        },
      })
        .then(getData)
        .then(
          (response: { data?: Array<{ id: string; claimNumber: string }> }) =>
            response?.data?.[0] ?? null
        ),
  }),
  reserveTotal: (claimId: string) => ({
    queryKey: ['reserve-total', claimId],
    queryFn: () => getClaimReserveTotal(claimId).then(getData),
  }),
  invoiceTotalPaid: (claimId: string) => ({
    queryKey: ['invoice-total-paid', claimId],
    queryFn: () => getClaimInvoiceTotalPaid(claimId).then(getData),
  }),
  listClaimsFiles: (filters: Record<string, unknown>) => ({
    queryKey: [{ filters }],
    queryFn: () => getAllMessages({ params: filters }).then(getData),
  }),
  notificationBadge: () => ({
    queryKey: ['bullepenBadge'],
    queryFn: () => getAllMessages({ params: notificationFilter }).then(getData),
  }),
});
